import { BioSampleId } from '@csp/csp-common-hbs-model';
import { MetaType, RestOptions, UserId } from '@csp/csp-common-model';
import { Page } from '@csp/dmdp-api-common-dto';
import { RefV1 } from '@csp/dmdp-api-device-dto';
import {
  BioSampleBatchCreateResultsV1,
  BioSampleBatchUpsertCustomStatusesResultsV1,
  BioSampleIdsAndCustomStatusesInV1,
  BioSampleInV1,
  BioSampleQueryV1,
  BioSamplesAndIdsInV1,
  BioSampleScheduleRequestRefV1,
  BioSamplesV1,
  BioSampleV1,
  ContainerInfoInV1,
  ContainerNumberInV1,
  CustomStatusesInV1,
  CustomStatusValueInV1,
  KitInfoInV1,
  MetaInV1,
  SampleCodeInV1,
  SampleKitCodeInV1,
} from '@csp/dmdp-api-hbs-dto';
import { biosamplingUrl } from '../../dmdp-config/dmdpConfig';

const bioSamplesUrl = (): string => `${biosamplingUrl()}/bio-sampling/v1/bio-samples`;

const createBioSample = async ({ axios }: RestOptions, inV1: BioSampleInV1): Promise<BioSampleV1> => {
  const resp = await axios.post(bioSamplesUrl(), inV1);
  return resp.data;
};

const createManyBioSamples = async (
  { axios }: RestOptions,
  inV1: BioSamplesAndIdsInV1,
): Promise<BioSampleBatchCreateResultsV1> => {
  const { data } = await axios.post(`${bioSamplesUrl()}:batch`, inV1);
  return data;
};

const setCustomStatus = async (
  { axios }: RestOptions,
  sampleId: BioSampleId,
  statusType: string,
  inV1: CustomStatusValueInV1,
): Promise<BioSampleV1> => {
  const resp = await axios.put(`${bioSamplesUrl()}/${sampleId}/custom-statuses/${statusType}`, inV1);
  return resp.data;
};

const setCustomStatusBatch = async (
  { axios }: RestOptions,
  sampleId: BioSampleId,
  insV1: CustomStatusesInV1,
): Promise<BioSampleV1> => {
  const resp = await axios.put(`${bioSamplesUrl()}/${sampleId}/custom-statuses`, insV1);
  return resp.data;
};

const setManyCustomStatuses = async (
  { axios }: RestOptions,
  inV1: BioSampleIdsAndCustomStatusesInV1,
): Promise<BioSampleBatchUpsertCustomStatusesResultsV1> => {
  const { data } = await axios.post(`${bioSamplesUrl()}/custom-statuses:batch`, inV1);
  return data;
};

const queryBioSamples = async (
  { axios, signal }: RestOptions,
  queryV1: BioSampleQueryV1,
  page?: Page,
): Promise<BioSamplesV1> => {
  const resp = await axios.post(`${bioSamplesUrl()}:query`, queryV1, {
    params: page,
    signal,
  });
  return resp.data;
};

const getById = async ({ axios, signal }: RestOptions, sampleId: BioSampleId): Promise<BioSampleV1> => {
  const response = await axios.get<BioSampleV1>(`${bioSamplesUrl()}/${sampleId}`, {
    signal,
  });
  return response.data;
};

const setBioSampleUserId = async (
  { axios }: RestOptions,
  sampleId: BioSampleId,
  userId: UserId,
): Promise<BioSampleV1> => {
  const response = await axios.put(`${bioSamplesUrl()}/${sampleId}/user-id`, { userId });
  return response.data;
};

const setBioSampleScheduleRequestRef = async (
  { axios }: RestOptions,
  sampleId: BioSampleId,
  scheduleRequestRef: BioSampleScheduleRequestRefV1,
): Promise<BioSampleV1> => {
  const response = await axios.put(`${bioSamplesUrl()}/${sampleId}/schedule-request-ref`, { scheduleRequestRef });
  return response.data;
};

const setBioSampleRef = async ({ axios }: RestOptions, sampleId: BioSampleId, refV1: RefV1): Promise<BioSampleV1> => {
  const response = await axios.put(`${bioSamplesUrl()}/${sampleId}/refs/${refV1.key}`, { value: refV1.value });
  return response.data;
};

const setBioSampleKitCode = async (
  { axios }: RestOptions,
  sampleId: BioSampleId,
  kitCodeInV1: SampleKitCodeInV1,
): Promise<BioSampleV1> => {
  const response = await axios.put(`${bioSamplesUrl()}/${sampleId}/sample-kit-code`, kitCodeInV1);
  return response.data;
};

const setBioSampleKitInfo = async (
  { axios }: RestOptions,
  sampleId: BioSampleId,
  kitInfoInV1: KitInfoInV1,
): Promise<BioSampleV1> => {
  const response = await axios.put(`${bioSamplesUrl()}/${sampleId}/kit-info`, kitInfoInV1);
  return response.data;
};

const setBioSampleContainerInfo = async (
  { axios }: RestOptions,
  sampleId: BioSampleId,
  containerInfoInV1: ContainerInfoInV1,
): Promise<BioSampleV1> => {
  const response = await axios.put(`${bioSamplesUrl()}/${sampleId}/container-info`, containerInfoInV1);
  return response.data;
};

const setBioSampleContainerNumber = async (
  { axios }: RestOptions,
  sampleId: BioSampleId,
  containerNumberInV1: ContainerNumberInV1,
): Promise<BioSampleV1> => {
  const response = await axios.put(`${bioSamplesUrl()}/${sampleId}/sample-container-number`, containerNumberInV1);
  return response.data;
};

const setBioSampleCode = async (
  { axios }: RestOptions,
  sampleId: BioSampleId,
  sampleCodeInV1: SampleCodeInV1,
): Promise<BioSampleV1> => {
  const response = await axios.put(`${bioSamplesUrl()}/${sampleId}/sample-code`, sampleCodeInV1);
  return response.data;
};

const setMeta = async (
  { axios }: RestOptions,
  bioSampleId: BioSampleId,
  metaType: MetaType,
  metaInV1: MetaInV1,
): Promise<BioSampleV1> => {
  const response = await axios.put(`${bioSamplesUrl()}/${bioSampleId}/metas/${metaType}`, metaInV1);
  return response.data;
};

const deleteMeta = async ({ axios }: RestOptions, bioSampleId: BioSampleId, metaType: MetaType): Promise<void> => {
  await axios.delete(`${bioSamplesUrl()}/${bioSampleId}/metas/${metaType}`);
};

export const BioSamplingRestServiceV1 = {
  createBioSample,
  createManyBioSamples,
  getById,
  queryBioSamples,
  setCustomStatus,
  setCustomStatusBatch,
  setManyCustomStatuses,
  setBioSampleUserId,
  setBioSampleScheduleRequestRef,
  setBioSampleRef,
  setBioSampleKitCode,
  setBioSampleKitInfo,
  setBioSampleContainerNumber,
  setBioSampleContainerInfo,
  setBioSampleCode,
  setMeta,
  deleteMeta,
};
